import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import CollapseIcon from 'core/icons/collapse';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  ExtensionProps,
} from 'pages/Dashboard/contexts/viewContext';
import Todo from 'pages/Dashboard/components/Todo';
import {
  usePatientToDos,
} from 'pages/Dashboard/pages/Todos/hooks';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Button from 'pages/Dashboard/components/Button';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import MediaListSkeleton from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/MediaListSkeleton';
import Pagination from 'core/components/VirtualTable/Pagination';

export type HeaderProps = {
  disabled: boolean;
  onAdd: () => void;
  onClose: () => void;
};

function Header({
  disabled,
  onAdd,
  onClose,
}: HeaderProps) {
  return (
    <div className="flex justify-between items-center !px-4 h-[60px]">
      <div className="flex items-center gap-2">
        <ToolTip title="Hide To-dos">
          <IconButton onClick={onClose}>
            <CollapseIcon className="!w-4 !h-4 !text-inherit" />
          </IconButton>
        </ToolTip>
        <h4 className="font-semibold text-sm">
          To-dos
        </h4>
      </div>
      <Button
        className="!font-semibold"
        variant="text"
        color="primary"
        type="button"
        onClick={onAdd}
        disabled={disabled}
        startIcon={<AddIcon className="!w-4 !h-4 !text-inherit" />}
      >
        Create To-do
      </Button>
    </div>
  );
}

const DEFAULT_PAGE_SIZE = 25;

export default function TodosPanel({
  patient,
  className,
  loaderSlot: _loaderSlot,
  onClose,
}: ExtensionProps) {
  const [page, setPage] = useState<number>(0);
  const [includeCompletedAndAbandoned, setIncludeCompletedAndAbandoned] = useState(false);
  const {
    data,
    refetch,
    isLoading,
  } = usePatientToDos({
    patientId: patient?.patientId,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    includeCompletedAndAbandoned,
  });

  const skipFilterRefetchRef = useRef(true);

  useEffect(() => {
    skipFilterRefetchRef.current = true;
  }, [patient?.patientId]);

  useEffect(() => {
    setPage(0);
  }, [patient?.patientId, includeCompletedAndAbandoned]);

  useEffect(() => {
    if (skipFilterRefetchRef.current) {
      skipFilterRefetchRef.current = false;
    } else {
      refetch();
    }
  }, [includeCompletedAndAbandoned, patient?.patientId, refetch]);

  const todos = data?.data ?? [];
  const pageProperties = data?.pageProperties ?? {};

  useSignalREffect('OnToDoUpdated', (toDoId) => {
    toDosEventChannel.emit('onToDosChanged', toDoId, patient?.patientId);
  }, [patient?.patientId]);

  useEffect(() => {
    const unsubscribe = toDosEventChannel.on('onToDosChanged', (toDoId, patientId) => {
      if (patientId === patient?.patientId) {
        refetch();
      }
    });
    return () => { unsubscribe(); };
  }, [patient?.patientId]);

  return (
    <div className={clsx('flex flex-col h-full ', className)}>
      <Header
        disabled={isLoading}
        onAdd={() => {
          toDosEventChannel.emit('onCreateToDo', { patient });
        }}
        onClose={onClose}
      />
      <div className="flex-1 flex flex-col overflow-y-auto">
        <div className="px-4 pb-4">
          <Checkbox
            id="include-completed-and-abandoned"
            checked={includeCompletedAndAbandoned}
            label="Include Completed & Abandoned"
            onChange={(checked) => {
              setIncludeCompletedAndAbandoned(checked);
            }}
          />
        </div>
        {isLoading && (<MediaListSkeleton className="px-4" />)}
        {(todos?.length === 0) && !isLoading && (
          <span className="text-xs text-gray-light px-4">
            No To-dos
          </span>
        )}
        {todos?.length > 0 && (
          <div className="flex flex-col gap-2 h-full overflow-hidden">
            <div className="flex flex-col flex-1 px-4 overflow-y-auto">
              {todos?.map((todo) => (
                <Todo key={todo?.toDoId} todo={todo} />
              ))}
            </div>
            <Pagination
              pageIndex={page}
              pageSize={DEFAULT_PAGE_SIZE}
              setPageIndex={setPage}
              totalItems={pageProperties?.totalSize ?? 0}
              classes={{
                root: 'items-center py-1',
                pageSizeSelector: '!hidden',
                pageInfo: 'flex flex-row items-center justify-between !w-full',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
